import React, { useContext, useEffect, useMemo, useState } from 'react';
import { IPanelHeaderRenderer, IPanelProps, Panel, PanelType } from '@fluentui/react/lib/Panel';
import { CrLoadingOverlay } from '../cr/CrLoadingOverlay';
import { ViewFormCommandBar } from '../cr/ViewFormCommandBar';
import { IApp03Sprint, IApp03Task, IApp03Workflow, IProject } from '../../types';
import styles from '../../styles/cr.module.scss';
import { getTaskPriorityString, WorkflowType } from '../../refData/WorkflowType';
import { renderDate } from '../cr/ListRenderers';
import { FormCommandBar } from '../cr/FormCommandBar';
import { Dropdown, ISelectableOption, MessageBar, MessageBarType } from '@fluentui/react';
import { DataContext } from '../DataContext';
import { CrDropdown } from '../cr/CrDropdown';

interface CloseSprintFormProps {
    showForm?: boolean;
    sprintID?: number;
    onClosed?: () => void;
    sprint?: IApp03Sprint;
    onSaved?: () => void;
}

interface ICloseSprintFormData {
    PlannedSprintID: number | null;
    DecisionKey: number | null;
    SelectedTaskIDs: number[];
}

interface IErrorMessage {
    PlannedSprintID: string | null;
    DecisionKey: string | null;
    SelectedTaskIDs: string | null;
}

class ErrorMessage implements IErrorMessage {
    public PlannedSprintID = null;
    public DecisionKey = null;
    public SelectedTaskIDs = null;
}

export const CloseSprintForm: React.FC<CloseSprintFormProps> = ({ sprint, showForm, onClosed, onSaved }) => {
    const { dataServices } = useContext(DataContext);
    const [error, setError] = useState<string>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [ppProject, setPPProject] = useState<IProject>();
    const [workflow, setWorkflow] = useState<IApp03Workflow>();
    const [plannedSprints, setPlannedSprints] = useState<IApp03Sprint[]>([]);
    const [closeSprintCompleted, setCloseSprintCompleted] = useState<boolean>(false);
    const [disableContinueBtn, setDisableContinueBtn] = useState<boolean>(false);

    const [formData, setFormData] = useState<ICloseSprintFormData>({
        PlannedSprintID: null,
        DecisionKey: null,
        SelectedTaskIDs: []
    });

    const [errMessages, setErrMessages] = useState<IErrorMessage>(new ErrorMessage());


    useEffect(() => {
        const fetchData = async () => {
            if (sprint.WorkflowID > 0) {
                setLoading(true);
                try {
                    const workflow: IApp03Workflow = await dataServices.app03WorkflowService.read(
                        sprint.WorkflowID,
                        false,
                        false,
                        [
                            'App03Portfolio($select=Title)',
                            `App03Tasks($filter=SprintID eq ${sprint.ID} and WorkflowStage/SystemStage eq 'Active';$select=ID,Title;$expand=WorkflowStage($select=ID,SystemStage),Sprint($select=ID))`
                        ]
                    );


                    if (workflow?.WorkflowTypeID === WorkflowType.ProjectWS) {
                        //load project
                        const ppProject: IProject = await dataServices.projectService.read(Number(workflow.PPProjectID));
                        setPPProject(ppProject);
                    }
                    //console.log('workflow', workflow);
                    setWorkflow(workflow);

                    const plannedSprints: IApp03Sprint[] = await dataServices.app03SprintService.readAllPlannedSprints(sprint.WorkflowID);
                    setPlannedSprints(plannedSprints);

                } catch (error) {
                    console.error('Error fetching data:', error);
                }
                finally {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, []);


    const getSecondHeading = (): string => {

        let secondHeading: string = `Close sprint #${sprint.SprintNumber} - ${sprint.Title} in work stream ${workflow?.Title}, `;
        const portfolioName: string = workflow?.App03Portfolio?.Title ?? '';
        switch (workflow.WorkflowTypeID) {
            case WorkflowType.General:
                secondHeading += `portfolio ${portfolioName}`;
                break;
            case WorkflowType.ProjectWS:
                secondHeading += `project ${ppProject?.Title}, portfolio ${portfolioName}`;
                break;
            case WorkflowType.StudyPlanner:
                secondHeading += `portfolio ${portfolioName}`;
                break;
        }

        return secondHeading;
    };

    const getPlannedSprintDropdownOptions = (): ISelectableOption[] => {
        const options = plannedSprints.map(s => ({
            key: s.ID,
            text: `Sprint ${s.SprintNumber} - ${s.Title}`,
        }));

        return options;
    };

    const handleDropdownChange = (field: keyof ICloseSprintFormData, option?: ISelectableOption) => {
        if (!option) return;
        setFormData(prev => ({
            ...prev,
            [field]: option.key as number,
            ...(field === 'DecisionKey' ? { SelectedTaskIDs: [] } : {}) // reset selected tasks if decision changes
        }));
    };

    const handleTaskMultiSelectChange = (event: React.FormEvent<HTMLDivElement>, option?: ISelectableOption) => {
        if (!option) return;

        setFormData(prev => {
            let selected = [...prev.SelectedTaskIDs];
            const taskId = option.key as number;

            if (option.selected) selected.push(taskId);
            else selected = selected.filter(id => id !== taskId);

            return { ...prev, SelectedTaskIDs: selected };
        });
    };

    const getTaskOptions = (): ISelectableOption[] =>
        workflow?.App03Tasks?.map(t => ({
            key: t.ID,
            text: t.Title
        })) ?? [];


    const validateEntity = (): boolean => {
        let valid = true;
        const newErr = new ErrorMessage();

        if (plannedSprints.length > 0 && !formData.PlannedSprintID) {
            newErr.PlannedSprintID = 'Please select a planned sprint.';
            valid = false;
        }

        if (!formData.DecisionKey) {
            newErr.DecisionKey = 'Please select a decision.';
            valid = false;
        }

        if (formData.DecisionKey === 2 && formData.SelectedTaskIDs.length === 0) {
            newErr.SelectedTaskIDs = 'Please select at least one task to move to backlog.';
            valid = false;
        }

        setErrMessages(newErr);
        return valid;
    };

    const handleSave = async () => {
        if (!validateEntity()) return;

        setDisableContinueBtn(true);
        const payload = {
            SprintID: sprint?.ID,
            WorkflowID: sprint?.WorkflowID,
            PlannedSprintID: formData.PlannedSprintID,
            DecisionKey: formData.DecisionKey,
            TaskIDs: formData.SelectedTaskIDs.join(','),
        };

        try {
            setLoading(true);
            await dataServices.app03SprintService.closeSprint(payload);
            setCloseSprintCompleted(true);
        } catch (e: any) {
            console.error('Error closing sprint:', e);

            // Try to extract a more useful message from backend response
            const message =
                e?.response?.data?.Message ||
                e?.response?.data?.message ||
                (typeof e?.response?.data === 'string' ? e.response.data : null) ||
                e?.message ||
                'An unknown error occurred while closing the sprint.';

            setError(message);
        }
        finally {
            setLoading(false);
            setDisableContinueBtn(false);
        }
    };


    return (
        <Panel
            isOpen={showForm}
            headerText={'Close Current Sprint'}
            type={PanelType.medium}
            onRenderHeader={(props: IPanelProps, defaultRender: IPanelHeaderRenderer) =>
                <>
                    {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
                    <div className={styles.panelHeaderText}>{defaultRender(props)}</div>
                </>
            }
            onRenderNavigation={() =>
                <FormCommandBar
                    primaryText='Continue'
                    cancelText={closeSprintCompleted ? 'Finish' : 'Cancel'}
                    primaryDisabled={disableContinueBtn}
                    onSave={handleSave}
                    onCancel={closeSprintCompleted ? onSaved : onClosed}
                    hidePrimary={!plannedSprints || plannedSprints.length === 0 || closeSprintCompleted}
                />
            }
        >
            <div className={styles.cr}>
                <CrLoadingOverlay isLoading={loading} opaque={true} />
                {workflow && (
                    <div>
                        <span>{getSecondHeading()}</span>

                        <div style={{ marginTop: '20px' }}>
                            {plannedSprints && plannedSprints.length > 0 ? (

                                <>
                                    {
                                        closeSprintCompleted ?
                                            (<div style={{ fontWeight: 'bold' }}>
                                                Action Completed and new sprint started.
                                            </div>) :
                                            (<>

                                                <CrDropdown
                                                    label='Select planned sprint'
                                                    className={styles.formField}
                                                    options={getPlannedSprintDropdownOptions()}
                                                    selectedKey={formData.PlannedSprintID ?? undefined}
                                                    onChange={(_, o) => handleDropdownChange('PlannedSprintID', o)}
                                                    errorMessage={errMessages.PlannedSprintID}
                                                />

                                                <CrDropdown
                                                    label={`Decide what to do with the ${workflow?.App03Tasks?.length} incomplete tasks in this sprint`}
                                                    className={styles.formField}
                                                    options={[
                                                        { key: 1, text: 'Move all to work stream Backlog' },
                                                        { key: 2, text: 'Select incomplete tasks to move to Backlog' },
                                                    ]}
                                                    selectedKey={formData.DecisionKey ?? undefined}
                                                    onChange={(_, o) => handleDropdownChange('DecisionKey', o)}
                                                    errorMessage={errMessages.DecisionKey}
                                                />

                                                {formData.DecisionKey === 2 && (
                                                    <>
                                                        <Dropdown
                                                            label='Select tasks to move to backlog'
                                                            multiSelect
                                                            options={getTaskOptions()}
                                                            selectedKeys={formData.SelectedTaskIDs}
                                                            onChange={handleTaskMultiSelectChange}
                                                            errorMessage={errMessages.SelectedTaskIDs}
                                                        />
                                                        <div style={{ fontStyle: 'italic', marginTop: '5px', color: 'gray' }}>
                                                            Note: any tasks not selected will be moved to the next sprint
                                                        </div>
                                                    </>
                                                )}

                                                <div style={{ fontWeight: 'bold', marginTop: '30px' }}>
                                                    Please click 'Continue' button above to move incomplete tasks to backlog as required, mark current sprint to closed and selected planned sprint as current.
                                                </div>
                                            </>)
                                    }

                                </>
                            ) : (
                                <div style={{ fontWeight: 'bold' }}>
                                    No planned sprints found to move to. Please press Cancel, create a new planned sprint and then close this sprint.
                                </div>
                            )}
                        </div>



                    </div>
                )}
            </div>
        </Panel>
    );
};